<script setup lang="ts">
import type { InventoryMapActionType, InventoryMapObjectTypes } from './../model/types.ts'
import { VIcons, VMapSettings } from '@/shared/ui'
import { computed, mergeProps, type PropType, watch } from 'vue'
import { InventObject } from '@/widgets'
import { useAuth } from '@/entities/Auth'
import { InventMapTypes } from '@/entities/Inventory/MapTypes'
import { WInventoryMapFilter } from '@/widgets/InventoryMapFilter'
import { useTranslate } from '@/shared/lib'
import { MapLayerPermissions } from '@/entities/Role'
import { ObjectMapPrintButton } from '@/features/Inventory/ObjectMapPrint'

const props = defineProps({
  zoom: {
    type: Number,
    required: true,
  },
  items: {
    type: Array as PropType<InventoryMapObjectTypes[]>,
    default: [],
  },
  objectItems: {
    type: Array as PropType<InventObject[]>,
    default: [],
  },
  ymapsAvailable: {
    type: Boolean,
    default: false,
  },
})

const { isPermissions } = useAuth()
const { trs } = useTranslate()
const auth = useAuth()

const emits = defineEmits(['print'])

const action = defineModel<InventoryMapActionType>({ required: true })
const addObjectType = defineModel('add_object_type', { required: true })
const isFull = defineModel('full', { required: true })
const filter = defineModel<any>('filter', { required: true })

const mapType = defineModel<'scheme' | 'satellite' | 'hybrid'>('map_type', { required: true })

const useAlternativeMap = defineModel<boolean>('alternative_map', { required: true })

/**
 * Отображаемое состояние переключателя альтернативной карты.
 * Если Yandex Maps недоступен — альтернатива включена принудительно и переключать её нельзя.
 */
const alternativeMapView = computed({
  get: () => useAlternativeMap.value || !props.ymapsAvailable,
  set: (value) => {
    useAlternativeMap.value = value
  },
})

const mapTypeOptions = [
  {
    value: 'scheme',
    icon: 'map',
    title: trs('itf.map_types.scheme'),
    permission: MapLayerPermissions.SCHEME,
  },
  {
    value: 'satellite',
    icon: 'satellite_alt',
    title: trs('itf.map_types.satellite'),
    permission: MapLayerPermissions.SATELLITE,
  },
  {
    value: 'hybrid',
    icon: 'layers',
    title: trs('itf.map_types.hybrid'),
    permission: MapLayerPermissions.HYBRID,
  },
] as const

const selectCoordsType = defineModel<'' | 'square' | 'circle'>('select_coords_type', {
  required: true,
})
const selectCoordsTypeOptions = [
  { value: 'square', icon: 'square', title: trs('itf.square') },
  { value: 'circle', icon: 'circle', title: trs('itf.circle') },
] as const

const isDisable = computed(() => props.zoom < 14)
const isMoveDisable = computed(() => false)

const removeTooltip = computed(() =>
  isDisable.value ? trs('itf.up_zoom_to_activate') : trs('itf.archive'),
)

const fullscreenTooltip = computed(() =>
  isFull.value ? trs('itf.map_action_exit_fullscreen') : trs('itf.map_action_fullscreen'),
)

watch(
  () => props.zoom,
  () => {
    if (props.zoom < 14) {
      action.value = 'select'
    }
  },
)

const clickMenuHandler = (type: InventoryMapObjectTypes) => {
  addObjectType.value = type
}

watch(action, () => {
  addObjectType.value = null
  selectCoordsType.value = ''
})

/* Вычисляем количество доступных слоев */
const isOnlyMapLayer = computed(() => auth.user?.map_layers_permissions.length == 1)

/* Вычисляем id/object_type_id для карты*/
const objectToPrint = computed(() =>
  props.objectItems.map((item) => ({
    id: item.getId(),
    object_type_id: item.getObjectTypeId(),
  })),
)
</script>

<template>
  <VBtnToggle
    color="warning"
    density="compact"
    v-model="action"
    mandatory
    divided
    variant="outlined"
  >
    <VTooltip location="top" :open-delay="500" :text="$t('itf.map_action_select')">
      <template #activator="{ props: tooltipProps }">
        <VBtn value="select" v-bind="tooltipProps">
          <VIcons type="touch_app" bold />
        </VBtn>
      </template>
    </VTooltip>

    <VMapSettings>
      <WInventoryMapFilter v-model="filter.types" v-model:field-filters="filter.fieldFilters" />
    </VMapSettings>

    <VTooltip location="top" :open-delay="500" :text="$t('itf.map_action_move')">
      <template #activator="{ props: tooltipProps }">
        <VBtn
          value="move"
          class="activator"
          :disabled="isMoveDisable || !isPermissions(['OBJECT_EDIT'])"
          v-bind="tooltipProps"
        >
          <VIcons type="drag_pan" bold />
        </VBtn>
      </template>
    </VTooltip>

    <InventMapTypes :disabled="isDisable" @selected="clickMenuHandler" />

    <VTooltip location="top" :open-delay="500" :text="removeTooltip">
      <template #activator="{ props: tooltipProps }">
        <VBtn
          value="remove"
          :disabled="isDisable || !isPermissions(['OBJECT_EDIT'])"
          v-bind="tooltipProps"
        >
          <VIcons type="archive" bold />
        </VBtn>
      </template>
    </VTooltip>

    <VTooltip location="top" :open-delay="500" :text="fullscreenTooltip">
      <template #activator="{ props: tooltipProps }">
        <VBtn v-bind="tooltipProps" @click.stop="isFull = !isFull">
          <VIcons type="open_in_full" bold />
        </VBtn>
      </template>
    </VTooltip>

    <VMenu v-if="!isOnlyMapLayer">
      <template #activator="{ props: menuProps }">
        <VTooltip location="top" :open-delay="500" :text="$t('itf.map_action_map_type')">
          <template #activator="{ props: tooltipProps }">
            <VBtn v-bind="mergeProps(menuProps, tooltipProps)">
              <VIcons :type="mapTypeOptions.find((o) => o.value === mapType)?.icon ?? 'map'" bold />
            </VBtn>
          </template>
        </VTooltip>
      </template>

      <VList density="compact">
        <VListItem v-if="auth.hasMapLayerPermission(MapLayerPermissions.ALTERNATIVE_MAP)">
          <VSwitch
            v-model="alternativeMapView"
            class="pl-2"
            :label="$t('itf.alternative_map')"
            :disabled="!ymapsAvailable"
            hide-details
            density="compact"
            @click.stop
          />
        </VListItem>

        <template v-for="option in mapTypeOptions" :key="option.value">
          <VListItem
            v-if="auth.hasMapLayerPermission(option.permission)"
            :active="mapType === option.value"
            @click="mapType = option.value"
          >
            <template #prepend>
              <VIcons :type="option.icon" bold />
            </template>

            {{ option.title }}
          </VListItem>
        </template>
      </VList>
    </VMenu>

    <VMenu>
      <template #activator="{ props: menuProps }">
        <VTooltip location="top" :open-delay="500" :text="$t('itf.select_area_tooltip')">
          <template #activator="{ props: tooltipProps }">
            <VBtn v-bind="mergeProps(menuProps, tooltipProps)">
              <VIcons type="search" bold />
            </VBtn>
          </template>
        </VTooltip>
      </template>

      <VList density="compact">
        <VListItem
          v-for="option in selectCoordsTypeOptions"
          :key="option.value"
          :active="selectCoordsType === option.value"
          @click="selectCoordsType = option.value"
        >
          <template #prepend>
            <VIcons :type="option.icon" />
          </template>

          {{ option.title }}
        </VListItem>
      </VList>
    </VMenu>

    <ObjectMapPrintButton :objects="objectToPrint" />
  </VBtnToggle>
</template>
